import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useLanguage } from "@/hooks/useLanguage"
import { es } from "@/locales/es"
import { en } from "@/locales/en"
import {
  Menu,
  X,
  Globe,
  ShoppingCart,
  LogOut,
  User as UserIcon,
  ChevronDown,
  ChevronRight,
  Sun,
  Moon,
  Search,
} from "lucide-react"
import { useTheme } from "next-themes"
import { useBrandTheme } from "@/hooks/useBrandTheme"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { BrandLogo } from "@/components/ui/brand-logo"
import { Input } from "@/components/ui/input"
import humanConsumptionImg from "@/assets/meat-cuts-human-consumption.jpg"
import industrialImg from "@/assets/pet-food-industrial.jpg"
import AdminLoginModal from "./AdminLoginModal"
import LoginModal from "./auth/LoginModal"
import CartSheet from "./CartSheet"
import { useCart } from "@/hooks/useCart"
import { useAuth, UserType, type AuthUser } from "@/hooks/useAuth"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu"
import { cn } from "@/lib/utils"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Settings, Shield, Zap } from "lucide-react"

const headerNavTriggerClassName =
  "text-primary dark:text-foreground text-sm font-semibold uppercase tracking-widest hover:text-accent dark:hover:text-accent transition-colors bg-transparent border-none p-0 h-auto hover:bg-transparent focus:bg-transparent focus:text-primary dark:focus:text-foreground data-[state=open]:bg-transparent data-[state=open]:text-primary dark:data-[state=open]:text-foreground data-[active]:bg-transparent data-[active]:text-primary dark:data-[active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:text-primary dark:data-[state=active]:text-foreground shadow-none"

const headerNavLinkClassName =
  "text-primary dark:text-foreground text-sm font-semibold uppercase tracking-widest hover:text-accent dark:hover:text-accent transition-colors bg-transparent border-none p-0 h-auto flex items-center focus:text-primary dark:focus:text-foreground shadow-none"

interface HeaderProps {
  /** @deprecated - language is now managed by LanguageContext. Remove when parent page is migrated. */
  language?: "es" | "en"
  /** @deprecated - language is now managed by LanguageContext. Remove when parent page is migrated. */
  onLanguageChange?: (lang: "es" | "en") => void
}

type NavType = {
  home: string
  shop: string
  industrialCatalog: string
  humanCatalog: string
  marketplace: string
  shopAll: string
  about: string
  ourStory: string
  services: string
  contact: string
  login: string
  signup: string
  tagline: string
  profile: string
  myAddresses: string
  logout: string
  dashboard: string
  vendorManagement: string
  myOrders: string
  adminPanel: string
}

const DesktopNav = ({ nav, user }: { nav: NavType; user: AuthUser | null }) => (
  <NavigationMenu>
    <NavigationMenuList className="gap-8">
      <NavigationMenuItem>
        <NavigationMenuTrigger className={headerNavTriggerClassName}>
          {nav.shop}
        </NavigationMenuTrigger>
        <NavigationMenuContent>
          <div className="grid w-[600px] grid-cols-2 gap-0 overflow-hidden rounded-none border border-border bg-card shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)]">
            <a
              href="/catalog/human_consumption"
              className="group relative aspect-[4/5] overflow-hidden border-r border-border/50"
            >
              <img
                src={humanConsumptionImg}
                alt={nav.humanCatalog}
                className="img-reveal h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-black/40 transition-colors group-hover:bg-black/10" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="font-display text-3xl leading-none tracking-widest text-white">
                  {nav.humanCatalog}
                </div>
                <div className="mt-2 h-1 w-0 bg-primary transition-all duration-500 group-hover:w-full" />
              </div>
            </a>
            <a href="/catalog/industrial" className="group relative aspect-[4/5] overflow-hidden">
              <img
                src={industrialImg}
                alt={nav.industrialCatalog}
                className="img-reveal h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-black/40 transition-colors group-hover:bg-black/10" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="font-display text-3xl leading-none tracking-widest text-white">
                  {nav.industrialCatalog}
                </div>
                <div className="mt-2 h-1 w-0 bg-primary transition-all duration-500 group-hover:w-full" />
              </div>
            </a>
          </div>
        </NavigationMenuContent>
      </NavigationMenuItem>

      <NavigationMenuItem>
        <NavigationMenuTrigger className={headerNavTriggerClassName}>
          {nav.about}
        </NavigationMenuTrigger>
        <NavigationMenuContent>
          <ul className="grid w-[240px] gap-0 overflow-hidden rounded-none border border-border bg-card shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)]">
            {[
              { label: nav.ourStory, href: "/about" },
              { label: nav.services, href: "/services" },
              { label: nav.contact, href: "/contact" },
            ].map((item) => (
              <li key={item.href} className="border-b border-border/50 last:border-0">
                <NavigationMenuLink asChild>
                  <a
                    href={item.href}
                    className="group flex items-center justify-between px-6 py-4 font-display text-lg tracking-widest transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    {item.label}
                    <ChevronRight className="h-4 w-4 -translate-x-2 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                  </a>
                </NavigationMenuLink>
              </li>
            ))}
          </ul>
        </NavigationMenuContent>
      </NavigationMenuItem>

      <NavigationMenuItem>
        <NavigationMenuLink
          href="/dashboard-vendor"
          className={headerNavLinkClassName}
        >
          {nav.vendorManagement}
        </NavigationMenuLink>
      </NavigationMenuItem>
    </NavigationMenuList >
  </NavigationMenu >
)

const MobileNav = ({ nav, user }: { nav: NavType; user: AuthUser | null }) => (
  <div className="flex flex-col space-y-8 py-6">

    <div className="space-y-6">
      <p className="border-b-2 border-foreground/5 pb-2 text-xs font-bold uppercase tracking-label text-muted-foreground">
        {nav.shop}
      </p>
      <div className="grid gap-6">
        <a href="/catalog/human_consumption" className="group flex items-center gap-4">
          <div className="h-16 w-16 shrink-0 overflow-hidden rounded-none border-2 border-foreground/10 transition-all group-hover:border-foreground/30">
            <img
              src={humanConsumptionImg}
              alt=""
              className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0"
            />
          </div>
          <span className="font-display text-2xl tracking-widest transition-opacity group-hover:opacity-80">
            {nav.humanCatalog}
          </span>
        </a>
        <a href="/catalog/industrial" className="group flex items-center gap-4">
          <div className="h-16 w-16 shrink-0 overflow-hidden rounded-none border-2 border-foreground/10 transition-all group-hover:border-foreground/30">
            <img
              src={industrialImg}
              alt=""
              className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0"
            />
          </div>
          <span className="font-display text-2xl tracking-widest transition-opacity group-hover:opacity-80">
            {nav.industrialCatalog}
          </span>
        </a>
      </div>
    </div>

    <div className="space-y-6">
      <p className="border-b-2 border-foreground/5 pb-2 text-xs font-bold uppercase tracking-label text-muted-foreground">
        {nav.about}
      </p>
      <div className="grid gap-4">
        {[
          { label: nav.ourStory, href: "/about" },
          { label: nav.services, href: "/services" },
          { label: nav.contact, href: "/contact" },
        ].map((item) => (
          <a
            key={item.label}
            href={item.href}
            className="font-display text-2xl tracking-widest transition-opacity hover:opacity-80"
          >
            {item.label}
          </a>
        ))}
      </div>
    </div>
  </div>
)

const LanguageToggle = ({
  language,
  onLanguageChange,
}: {
  language: "es" | "en"
  onLanguageChange: (lang: "es" | "en") => void
}) => (
  <div className="flex items-center gap-1 rounded-none border border-foreground/10 bg-muted/50 p-1 shadow-inner backdrop-blur-sm dark:bg-foreground/5">
    <Button
      variant="ghost"
      size="sm"
      onClick={() => onLanguageChange("es")}
      className={cn(
        "h-8 rounded-none px-3 py-1 font-display text-sm tracking-widest transition-all",
        language === "es"
          ? "bg-primary text-primary-foreground shadow-md hover:bg-primary/90"
          : "text-muted-foreground hover:bg-primary/5 hover:text-primary"
      )}
    >
      ES
    </Button>
    <Button
      variant="ghost"
      size="sm"
      onClick={() => onLanguageChange("en")}
      className={cn(
        "h-8 rounded-none px-3 py-1 font-display text-sm tracking-widest transition-all",
        language === "en"
          ? "bg-primary text-primary-foreground shadow-md hover:bg-primary/90"
          : "text-muted-foreground hover:bg-primary/5 hover:text-primary"
      )}
    >
      EN
    </Button>
  </div>
)

const ThemeToggle = () => {
  const { theme, setTheme } = useTheme()
  const { t } = useLanguage()

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      className="h-10 w-10 rounded-none border border-foreground/10 bg-muted/50 shadow-inner backdrop-blur-sm transition-all hover:border-primary hover:bg-primary/5 hover:text-primary dark:bg-foreground/5 dark:hover:bg-foreground/10"
      title={theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}
    >
      <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
      <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
      <span className="sr-only">{t.auth.toggleTheme}</span>
    </Button>
  )
}

const navigateToSearch = (
  navigate: ReturnType<typeof useNavigate>,
  query: string
) => {
  const trimmed = query.trim()
  if (!trimmed) return
  navigate(`/catalog/marketplace?q=${encodeURIComponent(trimmed)}`)
}

const SearchBar = () => {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [query, setQuery] = useState("")
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        navigateToSearch(navigate, query)
      }}
      role="search"
      className="hidden md:flex items-center bg-primary/5 dark:bg-foreground/5 rounded-full px-4 py-1.5 border border-primary/10 dark:border-border transition-colors focus-within:border-primary/40"
    >
      <button type="submit" aria-label={t.productCatalog.search} className="mr-2 text-primary/60 dark:text-foreground/60 hover:text-primary dark:hover:text-foreground transition-colors">
        <Search className="w-5 h-5" />
      </button>
      <input
        className="bg-transparent border-none focus:outline-none focus:ring-0 text-sm w-32 xl:w-48 placeholder:text-primary/40 dark:placeholder:text-foreground/40 text-primary dark:text-foreground transition-colors"
        placeholder={t.productCatalog.search}
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
    </form>
  )
}

const MobileSearchOverlay = ({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) => {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [query, setQuery] = useState("")
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (open) {
      setQuery("")
      const id = window.setTimeout(() => inputRef.current?.focus(), 50)
      return () => window.clearTimeout(id)
    }
  }, [open])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="top"
        className="md:hidden border-b border-primary/10 dark:border-border bg-bone/95 dark:bg-background-dark/95 backdrop-blur-md p-4"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>{t.productCatalog.search}</SheetTitle>
        </SheetHeader>
        <form
          role="search"
          onSubmit={(e) => {
            e.preventDefault()
            navigateToSearch(navigate, query)
            onOpenChange(false)
          }}
          className="pr-10 pt-2"
        >
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-primary/60 dark:text-foreground/60" />
            <Input
              ref={inputRef}
              className="w-full pl-10 pr-4 h-11 bg-primary/5 dark:bg-foreground/5 border-primary/10 dark:border-border rounded-full text-base placeholder:text-primary/40 dark:placeholder:text-foreground/40 text-primary dark:text-foreground"
              placeholder={t.productCatalog.search}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}

const BrandThemeToggle = () => {
  const { theme, setTheme } = useBrandTheme()
  const { t } = useLanguage()

  return (
    <div className="flex items-center gap-1 rounded-none border border-foreground/10 bg-muted/50 p-1 shadow-inner backdrop-blur-sm dark:bg-foreground/5">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setTheme("PROTO_FAUNA")}
        className={cn(
          "flex h-8 items-center gap-2 rounded-none px-3 py-1 font-display text-sm tracking-widest transition-all",
          theme === "PROTO_FAUNA"
            ? "bg-primary text-primary-foreground shadow-md hover:bg-primary/90"
            : "text-muted-foreground hover:bg-primary/5 hover:text-primary"
        )}
        title="Protocol Fauna (Default)"
      >
        <Zap className={cn("h-3 w-3", theme === "PROTO_FAUNA" ? "fill-current" : "")} />
        FAUNA
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setTheme("PROTO_INDUSTRIAL")}
        className={cn(
          "flex h-8 items-center gap-2 rounded-none px-3 py-1 font-display text-sm tracking-widest transition-all",
          theme === "PROTO_INDUSTRIAL"
            ? "bg-primary text-primary-foreground shadow-md hover:bg-primary/90"
            : "text-muted-foreground hover:bg-primary/5 hover:text-primary"
        )}
        title="Protocol Industrial (Tactical)"
      >
        <Shield className={cn("h-3 w-3", theme === "PROTO_INDUSTRIAL" ? "fill-current" : "")} />
        INDUSTRIAL
      </Button>
    </div>
  )
}

const Header = (_props: HeaderProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [showLogin, setShowLogin] = useState(false)
  const [showMobileSearch, setShowMobileSearch] = useState(false)
  const { totalItems, isCartOpen, setIsCartOpen } = useCart()
  const { user, logout } = useAuth()
  const { language, setLanguage, t } = useLanguage()

  const isSoftLaunch = import.meta.env.VITE_SOFT_LAUNCH === "true"
  const canAccessEcommerce = !isSoftLaunch || user?.userType === UserType.Admin

  const nav = t.header

  return (
    <header className="sticky top-0 z-50 bg-bone/95 dark:bg-background-dark/95 backdrop-blur-md border-b border-primary/10 dark:border-border px-6 lg:px-20 py-4 w-full transition-colors duration-300">
      <div className="max-w-[1440px] mx-auto flex items-center justify-between gap-8 h-full">

        {/* Mobile Menu & Search (Left side mobile) */}
        <div className="flex items-center md:hidden gap-1">
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            {/* Mobile Drawer */}
            <SheetContent side="left" className="w-[85vw] max-w-sm p-0 md:hidden border-r-primary/10 dark:border-r-border">
              <SheetHeader className="border-b border-primary/10 dark:border-border p-4 text-left">
                <SheetTitle className="industrial-font text-2xl text-primary dark:text-foreground">{t.common.menu}</SheetTitle>
              </SheetHeader>
              <ScrollArea className="h-[calc(100vh-80px)]">
                <div className="p-4 space-y-6">
                  <div className="flex flex-col space-y-8 py-6">
                    {canAccessEcommerce && (
                      <div className="space-y-6">
                        <p className="border-b-2 border-foreground/5 pb-2 text-xs font-bold uppercase tracking-label text-muted-foreground">
                          {nav.shop}
                        </p>
                        <div className="grid gap-6">
                          <a href="/catalog/human_consumption" className="group flex items-center gap-4">
                            <div className="h-16 w-16 shrink-0 overflow-hidden rounded-none border-2 border-foreground/10 transition-all group-hover:border-foreground/30">
                              <img
                                src={humanConsumptionImg}
                                alt=""
                                className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0"
                              />
                            </div>
                            <span className="font-display text-2xl tracking-widest transition-opacity group-hover:opacity-80">
                              {nav.humanCatalog}
                            </span>
                          </a>
                          <a href="/catalog/industrial" className="group flex items-center gap-4">
                            <div className="h-16 w-16 shrink-0 overflow-hidden rounded-none border-2 border-foreground/10 transition-all group-hover:border-foreground/30">
                              <img
                                src={industrialImg}
                                alt=""
                                className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0"
                              />
                            </div>
                            <span className="font-display text-2xl tracking-widest transition-opacity group-hover:opacity-80">
                              {nav.industrialCatalog}
                            </span>
                          </a>
                        </div>
                      </div>
                    )}

                    <div className="space-y-6">
                      <p className="border-b-2 border-foreground/5 pb-2 text-xs font-bold uppercase tracking-label text-muted-foreground">
                        {nav.about}
                      </p>
                      <div className="grid gap-4">
                        {[
                          { label: nav.ourStory, href: "/about" },
                          { label: nav.services, href: "/services" },
                          { label: nav.contact, href: "/contact" },
                        ].map((item) => (
                          <a
                            key={item.label}
                            href={item.href}
                            className="font-display text-2xl tracking-widest transition-opacity hover:opacity-80"
                          >
                            {item.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4 pt-4 border-t border-primary/10 dark:border-border">
                    <div className="flex flex-col gap-4">
                      <div className="flex gap-2">
                        <LanguageToggle language={language} onLanguageChange={setLanguage} />
                        <ThemeToggle />
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </SheetContent>
          </Sheet>
          {canAccessEcommerce && (
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 text-primary dark:text-foreground hover:text-accent dark:hover:text-accent"
              onClick={() => setShowMobileSearch(true)}
              aria-label={t.productCatalog.search}
            >
              <Search className="h-5 w-5" />
            </Button>
          )}
        </div>

        {/* Navigation */}
        <nav className="hidden lg:flex items-center gap-8 flex-1">
          <NavigationMenu>
            <NavigationMenuList className="gap-8">
              {canAccessEcommerce && (
                <NavigationMenuItem>
                  <NavigationMenuTrigger className={headerNavTriggerClassName}>
                    {nav.shop}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="grid w-[600px] grid-cols-2 gap-0 overflow-hidden rounded-none border border-border bg-card shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)]">
                      <a
                        href="/catalog/human_consumption"
                        className="group relative aspect-[4/5] overflow-hidden border-r border-border/50"
                      >
                        <img
                          src={humanConsumptionImg}
                          alt={nav.humanCatalog}
                          className="img-reveal h-full w-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/40 transition-colors group-hover:bg-black/10" />
                        <div className="absolute bottom-6 left-6 right-6">
                          <div className="font-display text-3xl leading-none tracking-widest text-white">
                            {nav.humanCatalog}
                          </div>
                          <div className="mt-2 h-1 w-0 bg-primary transition-all duration-500 group-hover:w-full" />
                        </div>
                      </a>
                      <a href="/catalog/industrial" className="group relative aspect-[4/5] overflow-hidden">
                        <img
                          src={industrialImg}
                          alt={nav.industrialCatalog}
                          className="img-reveal h-full w-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/40 transition-colors group-hover:bg-black/10" />
                        <div className="absolute bottom-6 left-6 right-6">
                          <div className="font-display text-3xl leading-none tracking-widest text-white">
                            {nav.industrialCatalog}
                          </div>
                          <div className="mt-2 h-1 w-0 bg-primary transition-all duration-500 group-hover:w-full" />
                        </div>
                      </a>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              )}

              <NavigationMenuItem>
                <NavigationMenuTrigger className={headerNavTriggerClassName}>
                  {nav.about}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[240px] gap-0 overflow-hidden rounded-none border border-border bg-card shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)]">
                    {[
                      { label: nav.ourStory, href: "/about" },
                      { label: nav.services, href: "/services" },
                      { label: nav.contact, href: "/contact" },
                    ].map((item) => (
                      <li key={item.href} className="border-b border-border/50 last:border-0">
                        <NavigationMenuLink asChild>
                          <a
                            href={item.href}
                            className="group flex items-center justify-between px-6 py-4 font-display text-lg tracking-widest transition-colors hover:bg-accent hover:text-accent-foreground"
                          >
                            {item.label}
                            <ChevronRight className="h-4 w-4 -translate-x-2 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                          </a>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              {canAccessEcommerce && (
                <NavigationMenuItem>
                  <NavigationMenuLink
                    href="/dashboard-vendor"
                    className={headerNavLinkClassName}
                  >
                    {nav.vendorManagement}
                  </NavigationMenuLink>
                </NavigationMenuItem>
              )}
            </NavigationMenuList >
          </NavigationMenu >
        </nav>

        {/* Centered Logo */}
        <div className="flex items-center justify-center flex-1">
          <a href="/" className="flex flex-col items-center group transition-opacity hover:opacity-80">
            <div className="flex items-center gap-3">
              <BrandLogo className="h-8 md:h-10 text-primary dark:text-foreground" />
              <div className="industrial-font text-3xl text-primary dark:text-foreground tracking-tighter">{t.auth.meatbidLogo}</div>
            </div>
            <div className="h-0.5 w-12 bg-accent mt-1 transition-all group-hover:w-full"></div>
          </a>
        </div>

        {/* Search & User Icons */}
        <div className="flex items-center justify-end gap-6 flex-1">

          {canAccessEcommerce && <SearchBar />}


          <div className="flex items-center gap-1 md:gap-3">
            <div className="hidden sm:flex items-center gap-1">
              <LanguageToggle language={language} onLanguageChange={setLanguage} />
              <ThemeToggle />
            </div>

            {canAccessEcommerce && (
              <button
                onClick={() => setIsCartOpen(true)}
                className="text-primary dark:text-foreground hover:text-accent dark:hover:text-accent transition-colors relative"
              >
                <ShoppingCart className="h-6 w-6" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 bg-accent text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                    {totalItems}
                  </span>
                )}
              </button>
            )}

            <div className="flex items-center">
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="text-primary dark:text-foreground hover:text-accent dark:hover:text-accent transition-colors">
                      <UserIcon className="h-6 w-6" />
                      <span className="sr-only">{t.auth.userMenu}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-64 rounded-xl border border-border bg-popover p-0 shadow-xl"
                  >
                    {/* Dropdown content remains unchanged */}
                    <div className="border-b border-border/50 bg-muted/50 p-4 rounded-t-xl">
                      <p className="mb-1 font-display text-lg leading-none tracking-widest text-foreground">
                        {user.displayName}
                      </p>
                      <p className="label-industrial text-xs break-all">{user.email}</p>
                    </div>
                    <div className="p-1">
                      {user.userType === UserType.Vendor && canAccessEcommerce && (
                        <DropdownMenuItem
                          onClick={() => (window.location.href = "/dashboard-vendor")}
                          className="cursor-pointer py-3 font-display text-lg tracking-widest focus:bg-accent focus:text-accent-foreground rounded-md"
                        >
                          {nav.dashboard}
                        </DropdownMenuItem>
                      )}
                      {user.userType === UserType.Admin && (
                        <DropdownMenuItem
                          onClick={() => (window.location.href = "/dashboard-admin")}
                          className="cursor-pointer py-3 font-display text-lg tracking-widest focus:bg-accent focus:text-accent-foreground rounded-md"
                        >
                          {nav.adminPanel}
                        </DropdownMenuItem>
                      )}
                      {canAccessEcommerce && (
                        <>
                          <DropdownMenuItem
                            onClick={() => (window.location.href = "/order-tracking")}
                            className="cursor-pointer py-3 font-display text-lg tracking-widest focus:bg-accent focus:text-accent-foreground rounded-md"
                          >
                            {nav.myOrders}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => (window.location.href = "/profile/addresses")}
                            className="cursor-pointer py-3 font-display text-lg tracking-widest focus:bg-accent focus:text-accent-foreground rounded-md"
                          >
                            {nav.myAddresses}
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator className="bg-foreground/5" />
                      <DropdownMenuItem
                        onClick={logout}
                        className="cursor-pointer py-3 font-display text-lg tracking-widest text-destructive focus:bg-destructive focus:text-destructive-foreground rounded-md"
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        {nav.logout}
                      </DropdownMenuItem>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <button
                  onClick={() => setShowLogin(true)}
                  className="text-primary dark:text-foreground hover:text-accent dark:hover:text-accent transition-colors"
                >
                  <UserIcon className="h-6 w-6" />
                  <span className="sr-only">{nav.login}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} />
      <CartSheet isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
      <MobileSearchOverlay open={showMobileSearch} onOpenChange={setShowMobileSearch} />
    </header>
  )
}
export default Header

